import { action, computed, makeObservable } from 'mobx'

import { DeliveryFilterType, DeliveryStatus } from '~/client/graph'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { deliveryReportFilterTypes } from '~/client/src/shared/enums/DeliveryFilterType'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import { getDeliveryFilterTypeDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Delivery from '~/client/src/shared/models/Delivery'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ScannersStore from '~/client/src/shared/stores/domain/Scanners.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import { enumToList } from '~/client/src/shared/utils/converters'

import ReportsStore from '../../../../Reports.store'
import DeliveryReportListStore from '../../../ReportsList/DeliveryReport/DeliveryReportList.store'
import FormReportListStore from '../../../ReportsList/FormReportList/FormReportList.store'
import ScanStationListStore from '../../../ReportsList/ScanStation/ScanStationList.store'
import BaseReportsFilterStore, { ISourceMap } from './BaseReportsFilter.store'
import ReportsFilterStore from './ReportsFilter.store'

const EXCLUDED_STATUSES = [DeliveryStatus.Deleted]

export default class DeliveryReportFilterStore extends ReportsFilterStore {
  public constructor(
    eventsStore: DesktopEventsStore,
    instancesStore: ReportsStore,
    instancesListStore:
      | ScanStationListStore
      | FormReportListStore
      | DeliveryReportListStore,
    onShowChanged: (isShown: boolean, filterType: string) => void,
    scannersStore: ScannersStore,
    companiesStore: CompaniesStore,
    projectMembersStore: ProjectMembersStore,
    userProjectsStore: UserProjectsStore,
    locationAttributesStore: LocationAttributesStore,
    tagsStore: TagsStore,
  ) {
    super(
      eventsStore,
      instancesStore,
      instancesListStore,
      onShowChanged,
      scannersStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      locationAttributesStore,
      tagsStore,
    )

    makeObservable(this)
  }

  protected get availableInstances(): Delivery[] {
    return this.instancesStore.allDeliveries
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: BaseReportsFilterStore
  } {
    const map: { [filterType: string]: BaseReportsFilterStore } = {}

    deliveryReportFilterTypes.forEach(filterType => {
      const { appState } = this.eventsStore

      map[filterType] = new BaseReportsFilterStore(
        filterType,
        appState,
        this.sourceMapByFilterTypeMap[filterType],
        this.instancesListStore,
        this.onShowChanged,
        this.fieldsMap(),
        getDeliveryFilterTypeDisplayName,
        this.getOptionName,
        LocalStorageKey.DeliveryReportReportFilter,
      )
    })
    return map
  }

  @action.bound
  public resetAllFilters() {
    deliveryReportFilterTypes.forEach(type =>
      localStorage.removeItem(
        `${this.eventsStore.appState.activeProject.id}_${LocalStorageKey.DeliveryReportReportFilter}_${type}`,
      ),
    )
    this.eventsStore.dispatch(RESET_ALL_FILTERS)
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    const maps = deliveryReportFilterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.getDefaultSourceMapByType(filterType)
      return acc
    }, {})

    this.availableInstances.forEach(instance => {
      Object.keys(maps).forEach(filterType => {
        const map = maps[filterType]
        const optionIds = this.getOptionsIdsByFilterType(instance, filterType)

        optionIds.forEach(optionId => {
          map[optionId]?.push(instance.id)
        })
      })
    })
    return maps
  }

  public getOptionsIdsByFilterType(
    delivery: Delivery,
    filterType: string,
  ): string[] {
    switch (filterType) {
      case DeliveryFilterType.Company:
        return [delivery.company || UNASSIGNED_FILTER_OPTION]
      case DeliveryFilterType.Status:
        return [delivery.status]
    }
  }

  protected getDefaultSourceMapByType(type: DeliveryFilterType) {
    let sourceList: string[] = []

    switch (type) {
      case DeliveryFilterType.Status:
        sourceList = enumToList(DeliveryStatus).filter(
          s => !EXCLUDED_STATUSES.includes(s),
        )
        break
      case DeliveryFilterType.Company:
        sourceList = [
          ...this.companiesStore.allCompaniesIds,
          UNASSIGNED_FILTER_OPTION,
        ]
        break
    }

    return sourceList.reduce((acc, optionId) => {
      acc[optionId] = []
      return acc
    }, {})
  }

  protected fieldsMap() {
    return this.eventsStore.appState.deliveryReportFilters.fieldsMap
  }
}
